import type {
  OptionAttributeValueDto,
  OptionDto,
  TriggerType,
  TriggerVisibleType,
} from "../../domain/types/company/company-product-system";

/**
 * 옵션 조건부 표시(트리거) 판정
 *
 * 쇼핑몰 상세 페이지(pod24_frontend_shop/public/javascripts/product/detail_new.js)의
 * evaluateTrigger / updateAllAttributeVisibility 와 같은 규칙을 쓴다.
 * 관리자 미리보기와 실제 주문 화면이 다르게 보이면 옵션 설정을 검증할 수 없기 때문이다.
 */

/**트리거 조건 충족 여부. 트리거가 없으면 항상 충족으로 본다.*/
export const evaluateTrigger = (
  triggerValueIds: number[] | null | undefined,
  triggerType: TriggerType | null | undefined,
  selectedIds: Set<number>,
): boolean => {
  if (!triggerValueIds || triggerValueIds.length === 0) return true;

  const selectedCount = triggerValueIds.filter((id) => selectedIds.has(id)).length;
  const totalCount = triggerValueIds.length;

  switch (triggerType) {
    // 모든 트리거 값이 선택되어야 표시
    case "AND":
      return selectedCount === totalCount;
    // 하나라도 미선택이면 표시 (모두 선택되면 숨김)
    case "NAND":
      return selectedCount < totalCount;
    // 모두 미선택이어야 표시
    case "NOR":
      return selectedCount === 0;
    // 기본값(OR): 하나라도 선택되면 표시
    case "OR":
    default:
      return selectedCount > 0;
  }
};

/**표시 상태. HIDDEN은 그리지 않고, DISABLED는 보이되 선택할 수 없다.*/
export type TriggerState = "VISIBLE" | "DISABLED" | "HIDDEN";

const toState = (matched: boolean, visibleType: TriggerVisibleType | null | undefined): TriggerState => {
  if (matched) return "VISIBLE";
  return visibleType === "DISABLE" ? "DISABLED" : "HIDDEN";
};

/**부모가 가려지면 자식도 같은 상태를 물려받는다.*/
const inherit = (parent: TriggerState, own: TriggerState): TriggerState =>
  parent === "VISIBLE" ? own : parent;

export interface OptionVisibility {
  /**옵션 ID → 표시 상태*/
  options: Record<number, TriggerState>;
  /**속성 ID → 표시 상태*/
  attributes: Record<number, TriggerState>;
  /**값 ID → 표시 상태*/
  values: Record<number, TriggerState>;
  /**조건을 통과해 실제로 유효한 선택 (속성 ID 문자열 → 값 ID)*/
  effectiveSelection: Record<string, number>;
}

/**
 * 옵션·속성·값의 표시 상태를 한 번에 계산한다.
 *
 * 가려진 항목의 선택은 다른 조건의 근거가 될 수 없다.
 * 예를 들어 "후가공 종류 = 타공"일 때만 보이는 "타공옵션"에서 무언가를 골라 둔 뒤
 * 후가공 종류를 "열재단"으로 바꾸면, 타공옵션의 선택은 더 이상 유효하지 않으므로
 * 그 선택에 매달린 다른 속성들도 함께 사라져야 한다.
 * 그래서 선택 집합이 더 이상 변하지 않을 때까지 반복해 연쇄를 반영한다.
 *
 * 선택할 수 없는 상태(DISABLED)의 선택도 유효하지 않은 것으로 본다.
 */
export const resolveOptionVisibility = (
  options: OptionDto[],
  selection: Record<string, number>,
): OptionVisibility => {
  let current = selection;

  // 한 번 반복할 때마다 최소 한 개의 선택이 걸러지므로 선택 수 + 1회면 반드시 수렴한다
  const maxRounds = Object.keys(selection).length + 1;
  let result: OptionVisibility = {
    options: {},
    attributes: {},
    values: {},
    effectiveSelection: current,
  };

  for (let round = 0; round <= maxRounds; round += 1) {
    const selectedIds = new Set(Object.values(current));

    const optionStates: Record<number, TriggerState> = {};
    const attributeStates: Record<number, TriggerState> = {};
    const valueStates: Record<number, TriggerState> = {};

    options.forEach((option) => {
      const optionState = toState(
        evaluateTrigger(option.triggerAttributeValueIds, option.triggerType, selectedIds),
        option.triggerVisibleType,
      );
      optionStates[option.id] = optionState;

      option.attributes.forEach((attribute) => {
        const attributeState = inherit(
          optionState,
          toState(
            evaluateTrigger(
              attribute.triggerAttributeValueIds,
              attribute.triggerType,
              selectedIds,
            ),
            attribute.triggerVisibleType,
          ),
        );
        attributeStates[attribute.id] = attributeState;

        attribute.values.forEach((value: OptionAttributeValueDto) => {
          valueStates[value.id] = inherit(
            attributeState,
            toState(
              evaluateTrigger(value.triggerAttributeValueIds, value.triggerType, selectedIds),
              value.triggerVisibleType,
            ),
          );
        });
      });
    });

    // 조건을 통과하지 못한 속성·값의 선택을 걸러 낸다
    const nextSelection: Record<string, number> = {};
    Object.entries(current).forEach(([attributeId, valueId]) => {
      const attributeState = attributeStates[Number(attributeId)];
      const valueState = valueStates[valueId];
      // 미리보기에 없는 항목(직접 입력 등)은 상태를 알 수 없으므로 그대로 둔다
      const attributeOk = attributeState === undefined || attributeState === "VISIBLE";
      const valueOk = valueState === undefined || valueState === "VISIBLE";
      if (attributeOk && valueOk) nextSelection[attributeId] = valueId;
    });

    result = {
      options: optionStates,
      attributes: attributeStates,
      values: valueStates,
      effectiveSelection: nextSelection,
    };

    const settled =
      Object.keys(nextSelection).length === Object.keys(current).length &&
      Object.entries(nextSelection).every(([key, value]) => current[key] === value);
    if (settled) break;

    current = nextSelection;
  }

  return result;
};
